import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { IInsuranceCaseType } from '../../models/Case'
import {
  IInsuranceClaimFormModel,
  InsuranceClaimsListFilters,
  InsuranceObjectFilters,
} from '../../models/InsuranceClaim'
import {
  IUnitFilter,
  UnitSortFieldEnum,
} from 'Modules/HousingAssociation/OurCompany/shared/models/Unit'
import {
  IBuildingFilterModel,
  IBuildingSortFieldEnum,
} from 'Modules/HousingAssociation/CompanyInfo/shared/models/Buildings'
import { RootState, emptySplitApi } from 'shared/store/store'
import {
  GET_INSURANCE_BUILDING_FILTERS,
  GET_INSURANCE_CLAIMS,
  GET_INSURANCE_UNIT_FILTERS,
} from 'shared/constants/storeTags'
import { ISocketMessage, SocketMessageTypeEnum } from 'shared/models/Socket'
import { addToasterMessage } from 'shared/store/toaster'
import { getBbl } from 'shared/store/bbl'

const initialState = {
  contractId: null as string | null,
  caseTypeId: null as string | null,
  caseDraftId: null as string | null,
  insuranceCaseType: null as IInsuranceCaseType | null,
  initialInsuranceClaimForm: null as IInsuranceClaimFormModel | null,
  lastUpdated: null as Date | null,

  units: {
    filters: {
      take: 10,
      skip: 0,
      searchText: null,
      sortcolumn: UnitSortFieldEnum.address,
      sortorder: 'Ascending',
    } as IUnitFilter,
  },
  buildings: {
    filters: {
      take: 10,
      skip: 0,
      searchText: null,
      buildingType: undefined,
      sortColumn: IBuildingSortFieldEnum.buildingType,
    } as IBuildingFilterModel,
  },
  insuranceObject: {
    filters: {
      take: 10,
      skip: 0,
    } as InsuranceObjectFilters,
  },
  claimList: {
    filters: {
      skip: 0,
      take: 5,
    },
  },
}

const insuranceClaimSlice = createSlice({
  name: 'insuranceClaim',
  initialState,
  reducers: {
    setInitialInsuranceClaimForm: (
      state,
      action: PayloadAction<{ insuranceClaim: IInsuranceClaimFormModel }>
    ) => {
      state.initialInsuranceClaimForm = action.payload.insuranceClaim
    },
    updateContractId: (state, action: PayloadAction<{ id: string }>) => {
      state.contractId = action.payload.id
    },
    updateCaseTypeId: (state, action: PayloadAction<{ id: string }>) => {
      state.caseTypeId = action.payload.id
    },
    updateCaseDraftId: (
      state,
      action: PayloadAction<{ caseDraftId: string }>
    ) => {
      state.caseDraftId = action.payload.caseDraftId
    },
    updateInsuranceCaseType: (
      state,
      action: PayloadAction<{ type: IInsuranceCaseType | null }>
    ) => {
      state.insuranceCaseType = action.payload.type
    },
    updateLastUpdated: (state, action: PayloadAction<{ updatedAt: Date }>) => {
      state.lastUpdated = action.payload.updatedAt
    },
    updateBuildingsFilters: (
      state,
      action: PayloadAction<Partial<IBuildingFilterModel>>
    ) => {
      state.buildings.filters = {
        ...state.buildings.filters,
        ...action.payload,
      }
    },
    updateUnitsFilters: (
      state,
      action: PayloadAction<Partial<IUnitFilter>>
    ) => {
      state.units.filters = {
        ...state.units.filters,
        ...action.payload,
      }
    },
    updateInsuranceObjectFilters: (
      state,
      action: PayloadAction<Partial<InsuranceObjectFilters>>
    ) => {
      state.insuranceObject.filters = {
        ...state.insuranceObject.filters,
        ...action.payload,
      }
    },
    setClaimListFilters: (
      state,
      action: PayloadAction<InsuranceClaimsListFilters>
    ) => {
      state.claimList.filters = action.payload
    },
  },
})

export const updateUnitsFiltersThunk = createAsyncThunk(
  'insuranceClaim/updateUnitsFilters',
  async (filters: Partial<IUnitFilter>, { dispatch }) => {
    dispatch(insuranceClaimSlice.actions.updateUnitsFilters(filters))
    dispatch(emptySplitApi.util.invalidateTags([GET_INSURANCE_UNIT_FILTERS]))
  }
)

export const updateBuildingsFiltersThunk = createAsyncThunk(
  'insuranceClaim/updateBuildingsFilters',
  async (filters: Partial<IBuildingFilterModel>, { dispatch }) => {
    dispatch(insuranceClaimSlice.actions.updateBuildingsFilters(filters))
    dispatch(
      emptySplitApi.util.invalidateTags([GET_INSURANCE_BUILDING_FILTERS])
    )
  }
)

export const setInsuranceObjectFiltersThunk = createAsyncThunk(
  'insuranceClaim/setInsuranceObjectFilters',
  async (filters: InsuranceObjectFilters, { dispatch }) => {
    await dispatch(
      insuranceClaimSlice.actions.updateInsuranceObjectFilters(filters)
    )
    dispatch(emptySplitApi.util.invalidateTags([GET_INSURANCE_CLAIMS]))
  }
)

export const setClaimListFiltersThunk = createAsyncThunk(
  'insuranceClaim/setClaimListFilters',
  async (filters: InsuranceClaimsListFilters, { dispatch }) => {
    await dispatch(insuranceClaimSlice.actions.setClaimListFilters(filters))
    dispatch(emptySplitApi.util.invalidateTags([GET_INSURANCE_CLAIMS]))
  }
)

export const handleInsuranceClaimTransferStatusMessageThunk = createAsyncThunk(
  'insuranceClaim/setInsuranceClaimTransferStatusMessage',
  async (payload: ISocketMessage, { getState, dispatch }) => {
    const state = getState() as RootState
    const bbl = getBbl(state).data?.shortName

    dispatch(emptySplitApi.util.invalidateTags([GET_INSURANCE_CLAIMS]))

    if (
      payload.NotificationType ===
      SocketMessageTypeEnum.InsuranceCaseDraftTransferDone
    ) {
      return dispatch(
        addToasterMessage({
          text: 'Forsikringssak overført.',
          type: 'success',
        })
      )
    }

    return dispatch(
      addToasterMessage({
        text: `Overføring av forsikringssak feilet. Prøv på nytt eller kontakt ${bbl} for videre oppfølging.`,
        type: 'danger',
      })
    )
  }
)

export default insuranceClaimSlice
